import { defineAction } from '~/prix'
import { z } from 'zod'
import expression from '~/prix/types/expression'
import { getZodSchemaInfo } from '~/prix/types/zodType'
import { citizenHealthProfileShape } from '../../citizenHealthProfile/stats/citizenHealthProfileStats.definition'
import { citizenLocationShape, citizenShapeBase } from '../../citizen/stats/citizenShape'
import { prepareAssertions } from '~/packages/general/variable/prepareAssertions.util'
import { Variable } from '~/packages/general/variable/variables.typing'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import healthProfessionalStatsAction from './healthProfessionalStats.action.json' with { type: 'json' }
import { dateOrIsoString } from '~/prix/types/date'
import prefixProperties from '~/prix/utils/prefixProperties'
import { healthVariableBaseRelevance } from '~/packages/general/variable/healthVariableRelevance.util'

export const healthProfessionalShape = z.object({
  originalId: z.string().describe('ID original do profissional de saúde'),
  dataSourceId: z.number().describe('ID da fonte de dados'),
  professionalCns: z.string().describe('CNS do profissional de saúde').nullish(),
  professionalCpf: z.string().describe('CPF do profissional de saúde').nullish(),
  professionalTeamIne: z.string().describe('Time do profissional de saúde').nullish(),
  professionalCivilName: z.string().describe('Nome do profissional de saúde').nullish(),
  professionalSocialName: z.string().describe('Nome social do profissional de saúde').nullish(),
  professionalSex: z.string().describe('Sexo do profissional de saúde').nullish(),
  professionalBirthDate: z.string().describe('Data de nascimento do profissional de saúde').nullish(),
  professionalPhoneNumber: z.string().describe('Número de telefone do profissional de saúde').nullish(),
  ...prefixProperties('citizen.', citizenShapeBase.shape),
  ...prefixProperties('citizen.', citizenLocationShape.shape),
  ...prefixProperties('citizenHealth.', citizenHealthProfileShape.shape),
})

const healthProfessionalExpression = expression(healthProfessionalShape.shape)

export const healthProfessionalVariable: Variable = {
  key: 'healthProfessional',
  categoryKey: 'healthProfessional',
  actionDefinitionKey: healthProfessionalStatsAction.key,
  title: 'Cadastros por profissional de saúde',
  unitSingular: 'cadastro',
  unitPlural: 'cadastros',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: getZodSchemaInfo(healthProfessionalShape),
  filterableBy: getZodSchemaInfo(
    healthProfessionalShape.pick({
      /* CIDADÃO */
      'citizen.age': true,
      'citizen.sex': true,
      'citizen.deceased': true,
      /* SAÚDE */
      'citizenHealth.hasHypertension': true,
      'citizenHealth.hasDiabetes': true,
      'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions': true,
      'citizenHealth.isPregnant': true,
      'citizenHealth.smoker': true,
      'citizenHealth.skinColorOrRace': true,
      'citizenHealth.educationLevel': true,
      'citizenHealth.employmentStatus': true,
      'citizenHealth.homeless': true,
      'citizenHealth.hasPrivateHealthInsurance': true,
      'citizenHealth.nationality': true,
    })
  ),
  betterDensity: 'less',
  relevance: healthVariableBaseRelevance.healthProfessional,
  basicSelect: ['citizen.name', 'citizen.socialName'],
  detailedSelect: [
    'professionalCivilName',
    'professionalSocialName',
    'professionalTeamIne',
    'professionalCns',
    'professionalSex',
    'professionalBirthDate',
    'professionalPhoneNumber',
    /* CIDADÃO */
    'citizen.originalId',
    'citizen.sex',
    'citizen.birthDate',
    'citizen.phoneNumber',
    'citizen.cns',
    'citizen.cpf',
    'citizen.streetName',
    'citizen.streetNumber',
    'citizen.neighborhood',
    'citizen.complement',
    'citizen.referencePoint',
    'citizen.postalCode',
    'citizen.deceased',
    'citizen.latitude',
    'citizen.longitude',
    /* SAÚDE */
    'citizenHealth.email',
    'citizenHealth.isResponsibleFamilyMember',
    'citizenHealth.hasDiabetes',
    'citizenHealth.hasHypertension',
    'citizenHealth.hasMedicalProfessionalDiagnosisOfMentalConditions',
    'citizenHealth.isPregnant',
    'citizenHealth.smoker',
    'citizenHealth.skinColorOrRace',
    'citizenHealth.educationLevel',
    'citizenHealth.employmentStatus',
    'citizenHealth.homeless',
    'citizenHealth.hasPrivateHealthInsurance',
    'citizenHealth.nationality',
  ],
  timeSeries: {
    model: 'additive',
    fitDays: 365 * 10,
    layers: [
      { layer: 'trend', degree: 1 },
      { layer: 'calendarSeasonality', type: 'dayOfWeek', degree: 0 },
      { layer: 'calendarSeasonality', type: 'monthOfYear', degree: 0 },
      { layer: 'calendarSeasonality', type: 'holiday', degree: 0 },
      { layer: 'calendarSeasonality', type: 'dayOfMonth', degree: 0 },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
      { layer: 'harmonicSeasonality', optimization: { minimumDays: 30, lossFunction: 'gradientMeanSquaredError' } },
    ],
  },
  assertions: prepareAssertions(healthProfessionalExpression),
}

const healthProfessionalStatsDefinition = defineAction({
  ...healthProfessionalStatsAction,
  input: z.object({
    variable,
    highlight: healthProfessionalExpression.optional(),
    filter: healthProfessionalExpression.optional(),
    space: spaceForPoint().optional(),
    time: timeForCurrentOnly().optional(),
    select: select(healthProfessionalShape),
    assertions,
    analysis,
  }),
  output: z.any(),
  cache: input => {
    if (
      input.filter ||
      input.space?.delimitationType === 'custom' ||
      (input.time?.type &&
        ['customDateRange', 'customDateRangePredicted'].includes(input.time?.type))
    ) {
      return {}
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 2,
      },
    }
  },
})


export default healthProfessionalStatsDefinition
